import type { NormalizedCacheObject } from "@apollo/client";
import type { CachePersistor } from "apollo3-cache-persist";
import {
  ComponentProps,
  ComponentType,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

// Тип для контекста CachePersistor, включает persistor, функцию для его обновления и функцию для очистки кэша
export type CachePersistorContext = readonly [
  CachePersistor<NormalizedCacheObject> | undefined,
  Dispatch<SetStateAction<CachePersistor<NormalizedCacheObject> | undefined>>,
  () => void
];

// Создание контекста для CachePersistor, по умолчанию undefined
// eslint-disable-next-line @typescript-eslint/no-redeclare -- companion object
export const CachePersistorContext = createContext<
  CachePersistorContext | undefined
>(undefined);

// Хук для использования CachePersistorContext в компонентах
export const useCachePersistorContext = () => {
  const context = useContext(CachePersistorContext);

  // Выбрасывает ошибку, если контекст не был найден
  if (!context) {
    throw new Error("Missing CachePersistorProvider.");
  }

  return context;
};

// Компонент-провайдер для CachePersistorContext
export const CachePersistorProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  // Состояние для хранения CachePersistor
  const [persistor, setPersistor] = useState<
    CachePersistor<NormalizedCacheObject> | undefined
  >(undefined);

  // Функция для очистки кэша
  const clearCache = useCallback(() => {
    if (!persistor) {
      return;
    }

    persistor.purge();
  }, [persistor]);

  return (
    <CachePersistorContext.Provider
      value={[persistor, setPersistor, clearCache]}
    >
      {children}
    </CachePersistorContext.Provider>
  );
};

// Функция высшего порядка для оборачивания компонента в CachePersistorProvider
export const withCachePersistorProvider = <P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> => {
  return function WithCachePersistorProvider(
    props: ComponentProps<typeof WrappedComponent>
  ) {
    return (
      <CachePersistorProvider>
        <WrappedComponent {...props} />
      </CachePersistorProvider>
    );
  };
};
